import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
  MRT_GlobalFilterTextField as MrtGlobalFilterTextField,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
} from 'material-react-table';
import useAPICall from '../../hooks/useAPICall';
import { useConfigTabs } from '../../contexts/ConfigTabsContext';
import GlobalMaterialTable from './GlobalMaterialTable';

const getRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.attributes)) return data.attributes;
  if (Array.isArray(data?.model)) return data.model;
  if (Array.isArray(data?.models)) return data.models;
  if (Array.isArray(data?.matchRuleGroups)) return data.matchRuleGroups;
  if (Array.isArray(data?.businessRuleGroups)) return data.businessRuleGroups;
  return [];
};

const formatCellValue = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ') || '-';
  if (typeof value === 'object') {
    return value.label || value.name || value.id || JSON.stringify(value);
  }
  return value;
};

const getCellValue = (row, column) => {
  if (typeof column.render === 'function') {
    return column.render(row);
  }

  if (Array.isArray(column.keys)) {
    const value = column.keys
      .map((key) => row?.[key])
      .find((item) => item !== undefined && item !== null && item !== '');
    return formatCellValue(value);
  }

  return formatCellValue(row?.[column.key]);
};

const DEFAULT_CONFIG_COLUMN_WIDTH = 160;

const configTabsSx = {
  minHeight: 48,
  '& .MuiTabs-flexContainer': {
    gap: { xs: 1, sm: 2 },
  },
  '& .MuiTab-root': {
    minHeight: 48,
    minWidth: 'auto',
    px: { xs: 1.5, sm: 2.5 },
    py: 1,
    textTransform: 'none',
    fontWeight: 500,
    color: '#64748b',
    opacity: 1,
    borderRadius: 0,
    flexShrink: 0,
    transition: 'color 0.2s ease',
    '&.Mui-selected': {
      color: '#1E88E5',
      fontWeight: 600,
      backgroundColor: 'transparent',
    },
    '&:hover': {
      color: '#334155',
      backgroundColor: 'transparent',
    },
  },
};

const renderTruncatedCell = (value, align = 'left', wrap = false) => (
  <Tooltip title={String(value)}>
    <Box
      component="span"
      sx={{
        display: 'block',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: wrap ? 'visible' : 'hidden',
        textOverflow: wrap ? 'clip' : 'ellipsis',
        whiteSpace: wrap ? 'normal' : 'nowrap',
        wordBreak: wrap ? 'break-word' : 'normal',
        overflowWrap: wrap ? 'break-word' : 'normal',
        textAlign: align,
      }}
    >
      {value}
    </Box>
  </Tooltip>
);

const ApiTablePage = ({ title, apiConfig, columns, initialPayload, toolbarActions, refreshKey, muiTableBodyRowProps }) => {
  const { loading, error, data, execute } = useAPICall();
  const configTabs = useConfigTabs();
  const cacheKey = useMemo(
    () => `config-api-cache:${apiConfig.endpoint}:${JSON.stringify(initialPayload || {})}`,
    [apiConfig.endpoint, initialPayload]
  );
  const [cachedRows, setCachedRows] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const rows = useMemo(() => {
    const liveRows = getRows(data);
    return liveRows.length > 0 ? liveRows : cachedRows;
  }, [data, cachedRows]);
  const tableColumns = useMemo(
    () => (columns || []).map((column) => ({
      header: column.label,
      id: column.label,
      size: column.size ?? DEFAULT_CONFIG_COLUMN_WIDTH,
      minSize: column.minSize ?? column.minWidth ?? column.size ?? DEFAULT_CONFIG_COLUMN_WIDTH,
      maxSize: column.maxSize ?? column.maxWidth ?? column.size ?? DEFAULT_CONFIG_COLUMN_WIDTH,
      accessorFn: (row) => (column.cell ? row : getCellValue(row, column)),
      Cell: ({ cell, row }) => (column.cell
        ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: column.align === 'center' ? 'center' : 'flex-start', width: '100%' }}>
            {column.cell({ row })}
          </Box>
        )
        : renderTruncatedCell(cell.getValue(), column.align || 'left', column.wrap)),
      muiTableHeadCellProps: column.align ? { align: column.align } : undefined,
      muiTableBodyCellProps: column.align ? { align: column.align } : undefined,
    })),
    [columns]
  );

  const fetchData = useCallback(async () => {
    try {
      const response = await execute(apiConfig, initialPayload, {
        errorMessage: `Unable to load ${title.toLowerCase()}.`,
        retries: 1,
      });
      const nextRows = getRows(response);
      setCachedRows(nextRows);
      sessionStorage.setItem(cacheKey, JSON.stringify(nextRows));
    } catch (err) {
      void err;
    }
  }, [apiConfig, cacheKey, execute, initialPayload, title]);

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setCachedRows(parsed);
        }
      }
    } catch {
      setCachedRows([]);
    }
  }, [cacheKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  return (
    <Box sx={{ width: '100%' }}>
      {configTabs && (
        <Box sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Tabs
            value={configTabs.activeTab}
            onChange={configTabs.onTabChange}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: {
                backgroundColor: '#1E88E5',
                height: '3px',
                borderTopLeftRadius: '3px',
                borderTopRightRadius: '3px',
              }
            }}
            sx={configTabsSx}
          >
            {configTabs.tabItems.map((item) => (
              <Tab
                key={item.id}
                value={item.id}
                label={item.label}
                disableRipple
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Box sx={{ minHeight: 300 }}>
        <GlobalMaterialTable
          columns={tableColumns}
          data={rows}
          isLoading={loading}
          error={error}
          emptyMessage="No records found"
          initialState={{ showGlobalFilter: false, showColumnFilters: false }}
          layoutMode="semantic"
          tableTitle={title}
          tableCount={cachedRows.length}
          renderRightToolbarCustomActions={toolbarActions ? () => toolbarActions : undefined}
          enablePagination
          enableBottomToolbar
          manualPagination={false}
          page={pagination.pageIndex + 1}
          pageSize={pagination.pageSize}
          onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
          onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
          muiTableBodyRowProps={muiTableBodyRowProps}
        />
      </Box>
    </Box>
  );
};

ApiTablePage.propTypes = {
  title: PropTypes.string.isRequired,
  apiConfig: PropTypes.shape({
    endpoint: PropTypes.string.isRequired,
    method: PropTypes.string.isRequired,
  }).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      keys: PropTypes.arrayOf(PropTypes.string),
      render: PropTypes.func,
    })
  ).isRequired,
  initialPayload: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  toolbarActions: PropTypes.node,
  refreshKey: PropTypes.number,
  muiTableBodyRowProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

ApiTablePage.defaultProps = {
  initialPayload: null,
  toolbarActions: null,
  refreshKey: 0,
  muiTableBodyRowProps: undefined,
};

export default ApiTablePage;
